import { GMXGoogleMap } from '../google-map/google-map'
import { DealerModel, DealerDepartment, OpeningHour } from '../../models/dealer.model'
import { GMXDLDrawerHandle } from './drawer-handle'
import { GMXGoogleSearchAddress } from '../google-search-address/google-search-address'
import { getHoursMarkup, getCurrentOpenHoursMrkup } from './utils'
import { ShoppingLinkModel } from '../../models/shopping-link.model'
import { GMXGoogleAutocompleteSearchDetail } from '../../models/google-search-address.model'
import { sanitizeInput, sanitizeUrl } from '../../utils/sanitize'
import { BRAND_MAKE_CODES, setGbaalDealerAttr, toGbaalDealer } from '../../utils/gbaalDealerData'
import { extractPostalCode } from '../../utils/postalCode'
import { persistSearchedZip, whenLocationSingletonReady } from '../../utils/locationSingleton'

/**
 * Per-tab configuration used to render a single dealer-details tab pane (Sales/Service).
 */
interface DealerTabConfig {
	id: string
	contactListing: string
	departmentCode: string
	closedLabel: string
	shoppingLinks: ShoppingLinkModel[]
}

/**
 * Resolved dealer-search criteria forwarded to GBAAL analytics for a completed search.
 * All fields are optional so the same shape can describe term-, postal-code-, and
 * filter-driven searches.
 */
interface GbaalDealerSearchInput {
	postalCode?: string
	query?: string
	filters?: string[]
	sortBy?: string
}

/**
 * Dealer Locator
 */
export class GMXDealerLocator extends HTMLElement {
	searchPanelEl: HTMLElement
	googleSearchAddrEl: GMXGoogleSearchAddress
	gmxGoogleMapEl: GMXGoogleMap
	resultsContainerEl: HTMLElement
	dealerDetailsPanelEl: HTMLElement
	level2BackBtn: HTMLButtonElement
	drawerHandle: GMXDLDrawerHandle

	private static readonly SEARCH_TERM_PARAM = 'searchTerm'

	private placesService: google.maps.places.PlacesService

	private currentSearchedDealerList: DealerModel[]

	// Values from the embedded GMXGoogleMap element
	private language: string
	private region: string

	private dealerResults: DealerModel[]

	private static readonly SEARCH_PANEL_MAP_INSET = 16
	private static readonly MAP_MIN_VISIBLE_PX = 100

	private boundResultsContainerClickHandler = this._handleResultsContainerClick.bind(this)
	private boundLevel2BackBtnClickHandler = this._handleLevel2BackBtnClick.bind(this)
	private boundMapClickHandler = this._handleMapClick.bind(this)
	private boundSearchInputSearchHandler = this._handleSearchInputSearch.bind(this)

	private dlDomPurify

	/**
	 * Set the moment the visitor searches for themselves.  Seeding resolves the stored location
	 * asynchronously and can land after they have already searched, and re-running the stored
	 * zip at that point would throw away the search they are looking at.
	 */
	private visitorHasSearched = false

	/** Bumped on every disconnect, so work deferred by a connection that has since ended can tell. */
	private connectionGeneration = 0

	/** Load-time search still waiting on the Maps API, held so a disconnect can drop it. */
	private pendingApiSearchHandler: (() => void) | undefined

	get dealerApi(): string {
		return this.getAttribute('data-dealer-api-url')
	}

	get brands(): string {
		return this.getAttribute('data-brands')
	}

	get primaryContactListing(): string {
		return this.getAttribute('data-primary-contact-listing')
	}

	get salesShoppingLinks(): ShoppingLinkModel[] {
		try {
			return this.hasAttribute('data-sales-shopping-links')
				? JSON.parse(this.getAttribute('data-sales-shopping-links') || '[]')
				: []
		} catch {
			window.console.error(
				'ERROR: Failed to parse sales shopping links data attribute. Please ensure it is properly formatted JSON.'
			)
			return []
		}
	}

	get serviceShoppingLinks(): ShoppingLinkModel[] {
		try {
			return this.hasAttribute('data-service-shopping-links')
				? JSON.parse(this.getAttribute('data-service-shopping-links') || '[]')
				: []
		} catch {
			window.console.error(
				'ERROR: Failed to parse service shopping links data attribute. Please ensure it is properly formatted JSON.'
			)
			return []
		}
	}

	get offeringsToDisplay(): { code: string; label: string }[] {
		try {
			return this.hasAttribute('data-offerings-to-display')
				? JSON.parse(this.getAttribute('data-offerings-to-display') || '[]')
				: []
		} catch {
			window.console.error(
				'ERROR: Failed to parse offerings-to-display data attribute. Please ensure it is properly formatted JSON.'
			)
			return []
		}
	}

	get servicesToList(): string {
		return this.getAttribute('data-services-to-list')
	}
	set servicesToList(val: string) {
		this.setAttribute('data-services-to-list', val)
	}

	get labelData(): Record<string, string> {
		return this.hasAttribute('data-labels') ? JSON.parse(this.getAttribute('data-labels')) : {}
	}
	set labelData(val: Record<string, string>) {
		this.setAttribute('data-labels', JSON.stringify(val))
	}

	constructor() {
		super()

		this.dlDomPurify = { ...window.gmxDOMPurify }
	}

	connectedCallback(): void {
		this.searchPanelEl = this.querySelector('.search-panel')
		this.googleSearchAddrEl = this.querySelector('gmx-google-search-address') as GMXGoogleSearchAddress
		this.gmxGoogleMapEl = this.querySelector('gmx-google-map') as GMXGoogleMap
		this.resultsContainerEl = this.querySelector('.results-container')
		this.dealerDetailsPanelEl = this.querySelector('.dealer-details-panel')
		this.level2BackBtn = this.querySelector('.level2-back-btn')
		this.drawerHandle = this.querySelector('gmx-dl-drawer-handle') as GMXDLDrawerHandle

		this.language = this.gmxGoogleMapEl.getAttribute('data-language')?.toLowerCase()
		this.region = this.gmxGoogleMapEl.getAttribute('data-region')?.toLowerCase()

		this.resultsContainerEl.addEventListener('click', this.boundResultsContainerClickHandler)

		this.level2BackBtn.addEventListener('click', this.boundLevel2BackBtnClickHandler)

		this.gmxGoogleMapEl.addEventListener('click', this.boundMapClickHandler)

		this.googleSearchAddrEl.addEventListener('gmxGoogleAutocompleteSearch', this.boundSearchInputSearchHandler)

		this._initializeSearchState()
	}

	disconnectedCallback(): void {
		this.connectionGeneration++

		this.resultsContainerEl.removeEventListener('click', this.boundResultsContainerClickHandler)

		this.level2BackBtn.removeEventListener('click', this.boundLevel2BackBtnClickHandler)

		this.gmxGoogleMapEl.removeEventListener('click', this.boundMapClickHandler)

		this.googleSearchAddrEl.removeEventListener('gmxGoogleAutocompleteSearch', this.boundSearchInputSearchHandler)

		// `once` only clears this once the API loads, which it may never do.
		if (this.pendingApiSearchHandler) {
			window.removeEventListener('gmxGoogleMapApiLoaded', this.pendingApiSearchHandler)
			this.pendingApiSearchHandler = undefined
		}
	}

	/**
	 * Callback for when the user clicks within the search results container.  If they clicked on a dealer listing,
	 * then we need to show the details of that dealer.
	 * @param e {Event}
	 * @private
	 */
	private _handleResultsContainerClick(e: Event): void {
		const target = e.target as HTMLElement

		if (target.classList.contains('gmx-show-more-btn')) {
			this.showMoreDealers()
		} else if (!target.closest('a')) {
			// Every link inside a result card (phone, website, service) sits within the `.dealer`
			// button, so a click on one would otherwise also open the details panel.  Deferring to
			// any anchor keeps that true for links added later.
			let dealerEl: HTMLElement
			if (target.classList.contains('dealer')) {
				dealerEl = target
			} else {
				dealerEl = target.closest('.dealer')
			}

			if (dealerEl) {
				const selectedBac = dealerEl.getAttribute('data-dealer-bac')

				this._showDealerDetails(selectedBac)
			} else {
				window.console.error(`ERROR:  Dealer Element could not be found`)
			}
		}
	}

	/**
	 * Callback for when the user click's the 'back' button on the second level panel (ie, dealer details) and they
	 * want to go back to the listing.
	 * @private
	 */
	private _handleLevel2BackBtnClick(): void {
		this.searchPanelEl.classList.remove('show-dealer-details')
		this.gmxGoogleMapEl.setZoom(10) // todo:  should this be an option???
	}

	private _handleMapClick(e: Event): void {
		const target = e.target as HTMLElement

		if (target.classList.contains('gmx-pin-individual')) {
			const dealerData = JSON.parse(target.getAttribute('data-dealer-data')) as DealerModel
			this._showDealerDetails(dealerData.bac)
		}
	}

	/**
	 * Trigger a search by first geocoding the location string then trigger a search.
	 *
	 * Returns the place Google resolved the term to, so a caller acting on a visitor's own
	 * search can read a postal code out of it; `undefined` when nothing could be resolved.
	 * @param input
	 */
	async searchByAmbiguousTerm(input: string): Promise<google.maps.places.PlaceResult | undefined> {
		return await this.getPlaceFromAmbiguousTerm(input)
			.then(rslts => {
				const locationGeometry = rslts[0].geometry
				// Use the resolved formatted address from the Google response when available
				// so the URL reflects the canonical place string, falling back to the raw input.
				const resolvedSearchTerm = rslts[0].formatted_address || input
				this._updateSearchTermInUrl(resolvedSearchTerm)
				this.searchForDealers(locationGeometry.location.lat(), locationGeometry.location.lng())

				return rslts[0]
			})
			.catch((error): google.maps.places.PlaceResult | undefined => {
				this.displaySearchError(this.labelData?.genericSearchFailedMsg || 'Error message missing')
				window.console.warn(error)

				return undefined
			})
	}

	/**
	 * Try to geocode a location by random location string.
	 * @param input {string} - String value to geocode
	 */
	async getPlaceFromAmbiguousTerm(input: string): Promise<google.maps.places.PlaceResult[]> {
		// `PlacesService` accepts either a `google.maps.Map` instance or an `HTMLDivElement` for
		// attribution rendering.  Pass the map when it is built, or fall back to the element itself
		// for a visitor quick enough to search in the window between the Maps API enabling the
		// field and `initMap()` finishing.
		this.placesService = new google.maps.places.PlacesService(
			this.gmxGoogleMapEl.map ?? (this.gmxGoogleMapEl as unknown as google.maps.Map)
		)
		return new Promise((resolve, reject) => {
			this.placesService.findPlaceFromQuery(
				{
					query: input,
					fields: ['formatted_address', 'geometry'],
				},
				(rslts, status) => {
					if (status === 'OK') {
						resolve(rslts)
					} else {
						reject(status)
					}
				}
			)
		})
	}

	/**
	 * Based on a given latitide and longitude, trigger a search for dealers
	 * @param lat {number} - Latitude of the location to search
	 * @param lng {number} - Longitude of the location to search
	 */
	async searchForDealers(lat: number, lng: number): Promise<void> {
		this.currentSearchedDealerList = []

		const headers = new Headers({
			'Content-Type': 'application/json; charset=utf-8',
			clientapplicationid: 'quantum',
			locale: `en-us`,
		})

		let makeCodes = ''
		this.brands.split(',').forEach(brand => {
			// @ts-expect-error:  Fake (and better) enum
			if (BRAND_MAKE_CODES[brand]) {
				// @ts-expect-error:  Fake (and better) enum
				makeCodes += `${BRAND_MAKE_CODES[brand]},`
			}
		})

		let qsps = `makeCodes=${makeCodes.replace(/,$/, '')}&latitude=${lat}&longitude=${lng}&searchType=latLongSearch&distance=500&desiredCount=25`
		if (this.servicesToList) {
			qsps += `&serviceCodes=${this.servicesToList}`
		}

		try {
			await fetch(`${this.dealerApi}?${qsps}`, {
				method: 'GET',
				headers: headers,
				cache: 'no-cache',
			})
				.then(response => {
					if (!response.ok) {
						throw new Error(`Dealer API returned ${response.status}`)
					}
					return response.json()
				})
				.then(data => {
					const dealers: DealerModel[] = parseInt(data.resultCount, 10) > 0 ? data.payload.dealers : []

					if (dealers.length) {
						this.displayDealers(dealers.slice(0, 5), dealers.length > 5)

						this.currentSearchedDealerList = dealers

						// Set top of component to top of viewport if component is at least partially in view
						const componentRect = this.getBoundingClientRect()
						if (componentRect.top < window.innerHeight && componentRect.bottom > 0) {
							window.scrollTo({
								top: componentRect.top + window.scrollY,
behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
							})
						}
					} else {
						this.displaySearchError(this.labelData?.noDealersFoundMsg || 'Error message missing')
					}

					// Emit the GBAAL analytics event once results have resolved — including the
					// zero-result case (resultCount: 0, dealers: []). Fire-and-forget: it is a no-op
					// when GBAAL isn't loaded, so we never reach for `window.gbaal` directly.
					this._dispatchGbaalSearchEvent(
						{
							query: this._getCurrentSearchTerm(),
							filters: this.servicesToList ? this.servicesToList.split(',') : undefined,
						},
						dealers
					)
				})
		} catch (e) {
			window.console.error(`ERROR:  Failed to retrieve dealer results: ${e}`)
			this.displaySearchError(this.labelData?.genericSearchFailedMsg || 'Error message missing')
		}
	}

	/**
	 * Show dealers after a given search
	 * @param dealers {DealerModel[]} - List of dealers to display
	 */
	displayDealers(dealers: DealerModel[], includeShowMoreBtn?: boolean): void {
		this.googleSearchAddrEl.classList.remove('invalid')
		this.googleSearchAddrEl.classList.remove('has-search-error')

		// A new result set invalidates whatever dealer the details panel was showing.  Leaving it in
		// place would strand the previous dealer's `data-gbaal` on a still-visible panel and
		// misattribute every interaction inside it.
		this._resetDealerDetails()

		this.dealerResults = dealers

		this.resultsContainerEl.innerHTML = `
			<ul class="gmx-text-list layout-stacked style-title-plus-address">
				${dealers
					.map(dealer => {
						// Falls back to the dealer's website when no service link is configured, or when the
						// configured one is rejected as unsafe.
						const serviceLinkHref =
							this.primaryContactListing === 'service' && this.serviceShoppingLinks.length
								? this._buildShoppingLinkHref(dealer, this.serviceShoppingLinks[0].url)
								: ''

						return `<li>
						<button class="list-btn dealer" aria-label="select dealer" data-dealer-bac="${sanitizeInput(dealer.bac)}">
							<div class="gmx-list-title">${sanitizeInput(dealer.dealerName)}</div>
							<div class="gmx-list-address">${sanitizeInput(dealer.address.addressLine1)} ${sanitizeInput(dealer.address.addressLine2)} ${sanitizeInput(dealer.address.cityName)}, ${sanitizeInput(dealer.address.countrySubdivisionCode)} ${sanitizeInput(dealer.address.postalCode)}</div>
							<div class="gmx-list-distance">${dealer.distance.toFixed(1)} ${dealer.distanceProp.measurementUnit === 'MI' ? 'mi' : 'km'}</div>
							<div class="gmx-list-contact-info">
								<a class="gmx-btn gmx-btn-link-secondary dealer-phone-link" href="tel:${encodeURI(this.getDepartmentPhoneNumber(dealer, this.primaryContactListing === 'service' ? '2' : '1'))}">${sanitizeInput(this.getDepartmentPhoneNumber(dealer, this.primaryContactListing === 'service' ? '2' : '1'))}</a>
								${
									serviceLinkHref
										? ` • <a class="gmx-btn gmx-btn-link-secondary service-link" data-gbaal-dealer-exit href="${serviceLinkHref}" target="_blank" rel="noopener noreferrer">${sanitizeInput(this.serviceShoppingLinks[0].label)}</a>`
										: dealer.dealerUrl
											? ` • <a class="gmx-btn gmx-btn-link-secondary dealer-website-link" data-gbaal-dealer-exit href="${sanitizeUrl(dealer.dealerUrl)}" target="_blank" rel="noopener noreferrer">${sanitizeInput(this.labelData['websiteLabel']) || 'Label Missing'}</a>`
											: ''
								}
							</div>
						</button>
					</li>`
					})
					.join('')}
			</ul>
			${
				includeShowMoreBtn
					? `
				<div class="show-more-btn-cntr">
					<button class="gmx-btn gmx-btn-primary gmx-show-more-btn">${sanitizeInput(this.labelData?.showMoreDealersLabel) || 'Label Missing'}</button>
				</div>`
					: ''
			}`

		this.resultsContainerEl.querySelectorAll('.gmx-text-list > li').forEach((cardEl, idx) => {
			this._setCardGbaalData(cardEl, dealers[idx])
		})

		this.googleSearchAddrEl.setAttribute('data-theme', 'light')
		this.searchPanelEl.classList.add('has-results')

		if (this.drawerHandle && !window.matchMedia('screen and (min-width: 481px)').matches) {
			// Only on sm/mobile
			this.drawerHandle.anchorToMaxHeight()
		}

		const markers: google.maps.marker.AdvancedMarkerElementOptions[] = []
		dealers.forEach(dl => {
			const newNode = document.createElement('button')
			newNode.classList.add('gmx-pin-individual')
			newNode.setAttribute('data-dealer-data', JSON.stringify(dl))
			markers.push({
				position: {
					lng: dl.geolocation.longitude,
					lat: dl.geolocation.latitude,
				},
				content: newNode,
			})
		})

		this._plotDealersOnMap(markers)
	}

	/**
	 * Wait for the map to be usable, reporting whether it ever became one.  The locator is defined
	 * before `gmx-google-map`, so the child can still be a plain element without `whenReady`.
	 * @private
	 */
	private async _whenMapReady(): Promise<boolean> {
		const mapEl = this.gmxGoogleMapEl

		if (!mapEl) {
			return false
		}

		if (typeof mapEl.whenReady !== 'function') {
			await customElements.whenDefined('gmx-google-map')
			customElements.upgrade(mapEl)
		}

		if (typeof mapEl.whenReady !== 'function') {
			return false
		}

		await mapEl.whenReady()

		return true
	}

	/**
	 * Drop a pin per result on the map, fitting them into the area not covered by the search panel.
	 *
	 * Waits on the map rather than assuming it is built: a search can outrun it, and reaching for
	 * it that early lost every pin and threw out of `displayDealers()`, which `searchForDealers()`
	 * reported as a failed search over results that had in fact rendered.
	 *
	 * @param markers {google.maps.marker.AdvancedMarkerElementOptions[]} - One pin per dealer found
	 * @private
	 */
	private async _plotDealersOnMap(markers: google.maps.marker.AdvancedMarkerElementOptions[]): Promise<void> {
		const generation = this.connectionGeneration

		// Not `_searchStillApplies()`: that also rejects a visitor's own search, whose pins these
		// usually are.  Waiting on the map is what makes a disconnect reachable from here.
		if (!(await this._whenMapReady()) || !this.isConnected || generation !== this.connectionGeneration) {
			return
		}

		this._applySearchPanelMapPadding()
		this.gmxGoogleMapEl.setMarkers(markers)
	}

	/**
	 * Tell the map how much of itself the search panel covers so fit, cluster-zoom, and centering
	 * keep the visible center in the remaining view.  Desktop docks the panel on the right; below
	 * 900px it sits along the bottom.
	 * @private
	 */
	private _applySearchPanelMapPadding(): void {
		this.gmxGoogleMapEl.setViewportPadding(this._getSearchPanelMapPadding())
	}

	/**
	 * Pixel inset matching the search panel's overlap with the map, plus a small gap so pins are
	 * not flush against the panel edge.  Clamped so fitBounds always has a usable remaining view.
	 * @private
	 */
	private _getSearchPanelMapPadding(): google.maps.Padding {
		const empty: google.maps.Padding = { top: 0, right: 0, bottom: 0, left: 0 }

		if (!this.searchPanelEl || !this.gmxGoogleMapEl) {
			return empty
		}

		const mapRect = this.gmxGoogleMapEl.getBoundingClientRect()
		const panelRect = this.searchPanelEl.getBoundingClientRect()

		if (mapRect.width <= 0 || mapRect.height <= 0) {
			return empty
		}

		const overlapX = Math.max(0, Math.min(panelRect.right, mapRect.right) - Math.max(panelRect.left, mapRect.left))
		const overlapY = Math.max(0, Math.min(panelRect.bottom, mapRect.bottom) - Math.max(panelRect.top, mapRect.top))

		if (overlapX <= 0 || overlapY <= 0) {
			return empty
		}

		const inset = GMXDealerLocator.SEARCH_PANEL_MAP_INSET
		const minVisible = GMXDealerLocator.MAP_MIN_VISIBLE_PX
		const padding: google.maps.Padding = { ...empty }

		if (this.isMobile()) {
			padding.bottom = Math.min(Math.round(overlapY) + inset, Math.max(0, mapRect.height - minVisible))
		} else {
			padding.right = Math.min(Math.round(overlapX) + inset, Math.max(0, mapRect.width - minVisible))
		}

		return padding
	}

	/**
	 * Write the analytics dealer data for a single result card.
	 *
	 * The card button wraps the whole listing, so GBAAL — which labels an interaction with the
	 * clicked element's text content — would otherwise report the address, distance and phone
	 * number as the link's value.  Naming the dealer in the button's own `data-gbaal` overrides
	 * that, since publisher values win over GBAAL's derived ones.
	 *
	 * The links inside the button then need their own copy of the dealer data: GBAAL reads a
	 * single nearest `data-gbaal` and never merges across ancestors, so without one they would
	 * pick up the button's attribute and be labelled with the dealer name instead of their own
	 * phone number / website text.
	 *
	 * @param cardEl {Element} - The result card the dealer is listed in
	 * @param dealer {DealerModel} - Dealer that card lists
	 * @private
	 */
	private _setCardGbaalData(cardEl: Element, dealer: DealerModel): void {
		setGbaalDealerAttr(
			cardEl.querySelector('.dealer'),
			dealer,
			// Omit the key entirely rather than overriding GBAAL's derivation with an empty label.
			dealer.dealerName ? { value: dealer.dealerName } : undefined
		)

		cardEl.querySelectorAll('.gmx-list-contact-info a').forEach(linkEl => setGbaalDealerAttr(linkEl, dealer))
	}

	/**
	 * Empty the dealer details panel and return the search panel to the results listing, dropping the
	 * `data-gbaal` attribute so no stale dealer data is left behind for the analytics collectors.
	 * @private
	 */
	private _resetDealerDetails(): void {
		this.searchPanelEl.classList.remove('show-dealer-details')
		this.dealerDetailsPanelEl.innerHTML = ''
		this.dealerDetailsPanelEl.removeAttribute('data-gbaal')
	}

	/**
	 * When the user wants to see a specific dealers details, need to build out the dealer results panel based
	 * on a given bac
	 * @param bac {string} - BAC of the dealer to display
	 * @private
	 */
	private _showDealerDetails(bac: string): void {
		const dealerDetails: DealerModel = this.dealerResults.find(d => d.bac === bac)

		if (dealerDetails) {
			this.dlDomPurify.addHook('afterSanitizeAttributes', function (node: Element) {
				if (node.tagName !== 'A') {
					node.removeAttribute('target')
				}
			})

			const detailTabs: DealerTabConfig[] = [
				{
					id: 'sales-tab',
					contactListing: 'sales',
					departmentCode: '1',
					closedLabel: this.labelData?.salesClosedLabel || 'Label Missing',
					shoppingLinks: this.salesShoppingLinks,
				},
				{
					id: 'service-tab',
					contactListing: 'service',
					departmentCode: '2',
					closedLabel: this.labelData?.serviceClosedLabel || 'Label Missing',
					shoppingLinks: this.serviceShoppingLinks,
				},
			]

			this.dealerDetailsPanelEl.innerHTML = this.dlDomPurify.sanitize(
				`
				<div class="details-body">
					<div class="details-dealer-name">${dealerDetails.dealerName}</div>
					<div class="details-dealer-distance">${dealerDetails.distance.toFixed(1)} ${dealerDetails.distanceProp.measurementUnit === 'MI' ? 'mi' : 'km'}</div>
					<gmx-tabs class="gmx-tabs gmx-tabs-small" data-selected-tab-id="${this.primaryContactListing === 'sales' ? 'sales-tab' : 'service-tab'}">
						<ul role="tablist" aria-multiselectable="false">
							<li role="presentation"><button class="tab-link-btn" type="button" role="tab" aria-selected="${this.primaryContactListing === 'sales'}" aria-controls="sales-tab">${this.labelData?.salesLabel || 'Sales Label missing'}</button></li>
							<li role="presentation"><button class="tab-link-btn" type="button" role="tab" aria-selected="${this.primaryContactListing === 'service'}" aria-controls="service-tab">${this.labelData?.serviceLabel || 'Service Label missing'}</button></li>
						</ul>
					</gmx-tabs>
					<div class="gmx-tab-content-group">
						${detailTabs.map(tab => this._buildDealerTabPane(dealerDetails, tab)).join('')}
					</div>
				</div>
			`,
				{ ADD_TAGS: ['gmx-accordion', 'gmx-tabs', 'gmx-text'], ADD_ATTR: ['open', 'target', 'rel'] }
			)

			this.dlDomPurify.removeHook('afterSanitizeAttributes')

			// Covers every interaction in both tab panes: the phone links, the website link, and
			// the shopping CTAs.
			setGbaalDealerAttr(this.dealerDetailsPanelEl, dealerDetails)

			this.searchPanelEl.classList.add('show-dealer-details')

			if (this.drawerHandle && !window.matchMedia('screen and (min-width: 481px)').matches) {
				this.drawerHandle.ensureDetailsMinHeight()
			}

			this._applySearchPanelMapPadding()
			this.gmxGoogleMapEl.setCenterAndZoom(
				{
					lat: dealerDetails.geolocation.latitude,
					lng: dealerDetails.geolocation.longitude,
				},
				16
			)
		} else {
			window.console.error(`ERROR:  Could not find results for dealer ${bac}`)
		}
	}

	/**
	 * Build the markup for a single dealer-details tab pane (Sales/Service).  The two panes share an
	 * identical structure and differ only by the values carried on the passed-in `tab` config.
	 * @param dealer {DealerModel} - Dealer whose details are being rendered
	 * @param tab {DealerTabConfig} - Per-tab configuration (department code, labels, links, etc.)
	 * @private
	 */
	private _buildDealerTabPane(dealer: DealerModel, tab: DealerTabConfig): string {
		return `
			<div class="gmx-tab-content-pane" role="tabpanel" aria-hidden="${this.primaryContactListing !== tab.contactListing}" id="${tab.id}">
				<div class="details-dealer-location">
					<div class="icon-location"></div>
					${sanitizeInput(`${dealer.address.addressLine1} ${dealer.address.addressLine2} ${dealer.address.cityName}`)}
				</div>
				<div class="details-dealer-phone">
					<div class="phone-summary">
						<div class="icon-phone"></div>
						<div>
							<a href="tel:${this.getDepartmentPhoneNumber(dealer, tab.departmentCode)}">
								${this.getDepartmentPhoneNumber(dealer, tab.departmentCode)}
							</a>
						</div>
						<div class="phone-label">
							${tab.departmentCode === '1' ? this.labelData?.salesLabel || 'Sales Label missing' : this.labelData?.serviceLabel || 'Service Label missing'}
						</div>
					</div>
				</div>
				<div class="details-dealer-hours">
					<gmx-accordion class="layout-stacked style-sm">
						<details>
							<summary>
								<div class="icon-clock"></div>
								<div class="hour-label">${this.labelData['hoursLabel'] || 'Label Missing'}</div>
								${getCurrentOpenHoursMrkup(this.getOperatingHoursForDepartment(dealer, tab.departmentCode), this.labelData, tab.departmentCode)}
							</summary>
							<div class="accordion-content">
								<div class="department">
									${getHoursMarkup(this.getOperatingHoursForDepartment(dealer, tab.departmentCode), tab.closedLabel, this.language)}
								</div>
							</div>
						</details>
					</gmx-accordion>
				</div>
				<div class="details-dealer-offerings">
					<gmx-accordion class="layout-stacked style-sm">
						<details>
							<summary>
								<div class="icon-list"></div>
								<div class="offerings-label">${this.labelData['offeringsLabel'] || 'Label Missing'}</div>
							</summary>
							<div class="accordion-content">
								<gmx-text class="gmx-paragraph1 gmx-colors-content-secondary gmx-no-list-decoration">
									<ul>
										${this._buildOfferingsMarkup(dealer)}
									</ul>
								</gmx-text>
							</div>
						</details>
					</gmx-accordion>
				</div>
				${
					dealer.dealerUrl
						? `<div class="details-dealer-website">
					<div class="icon-link"></div>
					<a data-gbaal-dealer-exit href="${sanitizeUrl(dealer.dealerUrl)}" target="_blank" rel="noopener noreferrer">${this.labelData['websiteLabel'] || 'Label Missing'}</a>
				</div>`
						: ''
				}
				<div class="cta-container">
					${this._buildShoppingLinksMarkup(dealer, tab.shoppingLinks)}
				</div>
			</div>`
	}

	/**
	 * Build the `<li>` markup for the dealer's offerings, limited to those offerings configured for
	 * display (`offeringsToDisplay`) that the dealer actually provides (matched on service code).
	 * @param dealer {DealerModel} - Dealer whose services are matched against the configured offerings
	 * @private
	 */
	private _buildOfferingsMarkup(dealer: DealerModel): string {
		let offeringsMarkup = ''
		this.offeringsToDisplay.forEach(offering => {
			if (dealer.services?.some(service => service.code === offering.code)) {
				offeringsMarkup += `<li>${sanitizeInput(offering.label)}</li>`
			}
		})
		return offeringsMarkup
	}

	/**
	 * Build a shopping link's href for a given dealer, appending the dealer's BAC as a query string
	 * parameter to the sanitized URL.  `sanitizeUrl()` rejects anything that isn't an http(s) or
	 * same-origin relative URL, so authored links can never introduce an unsafe scheme.
	 * @param dealer {DealerModel} - Dealer used to derive the `bacId` query parameter
	 * @param url {string} - Configured shopping link URL
	 * @private
	 */
	private _buildShoppingLinkHref(dealer: DealerModel, url: string): string {
		const sanitizedUrl = sanitizeUrl(url)

		// `sanitizeUrl()` returns an empty string for anything it rejects.  Appending the BAC to that
		// would leave a relative link back to the current page, so callers get an empty href instead
		// and drop the link entirely.
		if (!sanitizedUrl) {
			return ''
		}

		// `searchParams` encodes the value and keeps it in the query string ahead of any `#fragment`,
		// which plain concatenation would push the BAC behind.
		const shoppingUrl = new URL(sanitizedUrl)
		shoppingUrl.searchParams.set('bacId', dealer.bac)

		return shoppingUrl.toString()
	}

	/**
	 * Build the CTA anchor markup for a set of shopping links, appending the dealer's BAC as a query
	 * string parameter and styling the first link as primary and the rest as tertiary.
	 * @param dealer {DealerModel} - Dealer used to derive the `bacId` query parameter
	 * @param shoppingLinks {ShoppingLinkModel[]} - Links to render (e.g. sales or service links)
	 * @private
	 */
	private _buildShoppingLinksMarkup(dealer: DealerModel, shoppingLinks: ShoppingLinkModel[]): string {
		return shoppingLinks
			.map((sl: ShoppingLinkModel) => ({ link: sl, href: this._buildShoppingLinkHref(dealer, sl.url) }))
			.filter(({ href }) => href)
			.map(({ link, href }, idx: number) => {
				const aEl = document.createElement('a')
				aEl.classList.add('gmx-btn', idx === 0 ? 'gmx-btn-primary' : 'gmx-btn-tertiary', 'size-lg')
				aEl.setAttribute('href', href)
				aEl.setAttribute('data-gbaal-dealer-exit', '')
				aEl.setAttribute('target', link.openInNewTab === false ? '_self' : '_blank')
				if (link.openInNewTab !== false) {
					aEl.setAttribute('rel', 'noopener noreferrer')
				}
				aEl.innerText = link.label
				return aEl.outerHTML
			})
			.join('')
	}

	/**
	 * Callback for when a search failed for some reason.
	 * @param errorMsg {string} - Error message to display under the input field.
	 */
	displaySearchError(errorMsg: string): void {
		this.searchPanelEl.classList.add('has-search-error')
		this._resetDealerDetails()
		this.googleSearchAddrEl.setAttribute('data-theme', 'light')

		if (this.googleSearchAddrEl) {
			this.googleSearchAddrEl.displayErrorMsg(errorMsg)
		}
	}

	isMobile(): boolean {
		return !window.matchMedia('screen and (min-width: 900px)').matches
	}

	getDepartmentPhoneNumber(dealer: DealerModel, departmentCode: string): string {
		let preferredPhone = ''

		if (dealer.departments.length) {
			const preferredContact: DealerDepartment = dealer.departments.filter(d => d.code === departmentCode)[0]
			if (preferredContact) {
				preferredPhone = preferredContact.phoneNumber
			} else {
				preferredPhone = dealer.departments[0].phoneNumber
			}
		} else {
			preferredPhone = dealer.generalContact.phone1
		}

		return preferredPhone
	}

	getOperatingHoursForDepartment(dealer: DealerModel, departmentCode: string): OpeningHour[] {
		let preferredHours: OpeningHour[] = []

		if (dealer.departments.length) {
			const preferredContact: DealerDepartment = dealer.departments.filter(d => d.code === departmentCode)[0]
			if (preferredContact) {
				preferredHours = preferredContact.departmentHours
			} else {
				preferredHours = dealer.departments[0].departmentHours
			}
		} else {
			preferredHours = dealer.generalOpeningHour
		}

		return preferredHours
	}

	showMoreDealers(): void {
		this.displayDealers(this.currentSearchedDealerList, false)
	}

	/**
	 * Read the resolved search term currently persisted in the URL (`?searchTerm=`). Used to
	 * populate the GBAAL analytics term. Returns `undefined` when absent or unreadable so the
	 * caller can fall back gracefully.
	 * @private
	 */
	private _getCurrentSearchTerm(): string | undefined {
		try {
			return new URLSearchParams(window.location.search).get(GMXDealerLocator.SEARCH_TERM_PARAM) ?? undefined
		} catch {
			return undefined
		}
	}

	/**
	 * Emit a fire-and-forget GBAAL analytics event describing a completed dealer search.
	 *
	 * GBAAL routes `dealerSearch` to its own push method, which owns the event's `eventData`
	 * vocabulary and validates the payload, and enriches the record with page/user/platform/geo/
	 * session context. This payload therefore carries only the search criteria and dealer data
	 * this component owns, in the exact shape the schema allows — both `search` and each entry in
	 * `dealers` are strict, so an unexpected key would cost the whole slice. When GBAAL isn't
	 * present the event is simply ignored — there is no hard dependency on `window.gbaal`.
	 * `composed: true` is required so the event escapes the Shadow DOM boundaries of nested
	 * custom elements.
	 *
	 * @param searchInput {GbaalDealerSearchInput} - Resolved search criteria for this query.
	 * @param results {DealerModel[]} - Dealers returned by the search (empty array when none found).
	 * @private
	 */
	private _dispatchGbaalSearchEvent(searchInput: GbaalDealerSearchInput, results: DealerModel[]): void {
		const search = {
			term: searchInput.postalCode ?? searchInput.query ?? '',
			// Describes the search the visitor performed, mirroring the event; GBAAL never infers it.
			type: 'dealer search',
			resultCount: results.length,
			...(searchInput.filters?.length ? { filterParams: searchInput.filters } : {}),
			...(searchInput.sortBy ? { sortBy: searchInput.sortBy } : {}),
		}

		const dealers = results.map(dealer => toGbaalDealer(dealer))

		const detail = {
			event: 'dealerSearch',
			payload: { search, dealers },
		}

		this.dispatchEvent(new CustomEvent('gbaal:event', { detail, bubbles: true, composed: true }))
	}

	private async _handleSearchInputSearch(e: CustomEvent<GMXGoogleAutocompleteSearchDetail>): Promise<void> {
		const details = e.detail

		this.visitorHasSearched = true

		if (details.location) {
			// `searchStr` here is the resolved formatted address from the Google
			// Places autocomplete (`place.formatted_address`) or the reverse-geocoded
			// address when using geolocation. Persist it to the URL.
			if (details.searchStr) {
				this._updateSearchTermInUrl(details.searchStr)
			}
			this.searchForDealers(details.location.lat, details.location.lng)

			persistSearchedZip(details.postalCode || extractPostalCode(details.searchStr), details)
		} else {
			// URL will be updated inside `searchByAmbiguousTerm()` once the location
			// service resolves the typed input to a formatted address.
			const place = await this.searchByAmbiguousTerm(details.searchStr)

			// A typed term is dispatched before anything is geocoded, so the postal code has to
			// come out of the place the search itself resolved.  A search that resolved to
			// nothing shares nothing: the raw term is whatever the visitor happened to type,
			// and Google just said it names no real place.
			persistSearchedZip(extractPostalCode(place?.formatted_address))
		}
	}

	/**
	 * Append/update the `searchTerm` query string parameter on the current URL while
	 * preserving the existing path and any other pre-existing query parameters.
	 * Uses `history.replaceState` so navigation history isn't polluted with each search.
	 *
	 * @param searchTerm {string} - Resolved search value to persist in the URL.
	 * @private
	 */
	private _updateSearchTermInUrl(searchTerm: string): void {
		if (!searchTerm || typeof window === 'undefined' || !window.history?.replaceState) {
			return
		}

		try {
			const url = new URL(window.location.href)
			url.searchParams.set(GMXDealerLocator.SEARCH_TERM_PARAM, searchTerm)
			window.history.replaceState(window.history.state, '', url.toString())
		} catch (err) {
			window.console.warn(`WARN: Failed to update searchTerm in URL: ${err}`)
		}
	}

	/**
	 * Decide what the page opens on. A `?searchTerm=` parameter reproduces the state a shared
	 * or reloaded URL describes and always wins; failing that, the location the visitor already
	 * gave another app on the site seeds the search so results are on screen without them
	 * having to say where they are twice. Neither present renders the default empty state.
	 * @private
	 */
	private _initializeSearchState(): void {
		const searchTerm = this._getCurrentSearchTerm()?.trim()

		if (searchTerm) {
			this._prefillAndSearch(searchTerm)
			return
		}

		this._seedFromStoredLocation()
	}

	/**
	 * Whether a search scheduled earlier should still go ahead.  Seeding resolves the stored
	 * location and then waits on the Google Maps API, and by the time either finishes the page
	 * may have moved on: the locator can be gone, so searching would rewrite the URL of a page
	 * that no longer shows one; the visitor can have searched for themselves, which outranks a
	 * value they gave another app; or the locator can have been detached and reattached, leaving
	 * this seed behind while the new connection starts its own.
	 * @param generation {number} - Connection the caller was scheduled under
	 * @private
	 */
	private _searchStillApplies(generation: number): boolean {
		return this.isConnected && !this.visitorHasSearched && generation === this.connectionGeneration
	}

	/**
	 * Open on the visitor's stored zip. Nothing is written back — the value is already stored,
	 * and persisting is reserved for searches the visitor actually performed.
	 * @private
	 */
	private async _seedFromStoredLocation(): Promise<void> {
		const generation = this.connectionGeneration
		const locationSingleton = await whenLocationSingletonReady()
		const location = await locationSingleton.resolve()

		if (location?.zip && this._searchStillApplies(generation)) {
			this._prefillAndSearch(location.zip)
		}
	}

	/**
	 * The search field, reached without going through `gmx-google-search-address`'s own `inputEl`.
	 * Custom elements upgrade parent-first, so when the locator opens on a term the child has not
	 * run its `connectedCallback` yet and that property is still undefined — which silently skipped
	 * the prefill and left the field empty on every shared or reloaded URL.
	 * @private
	 */
	private _getSearchInput(): HTMLInputElement | undefined {
		return this.googleSearchAddrEl?.inputEl ?? this.googleSearchAddrEl?.querySelector('input') ?? undefined
	}

	/**
	 * Put a term in the search field and run it. The field is populated immediately so it reflects
	 * the state right away.  The search waits on the Maps API only, all `searchByAmbiguousTerm`
	 * needs; waiting on the map would gate it on a sibling that may not have been upgraded yet.
	 * @param searchTerm {string} - Term to search for on the visitor's behalf
	 * @private
	 */
	private _prefillAndSearch(searchTerm: string): void {
		const generation = this.connectionGeneration
		const inputEl = this._getSearchInput()

		if (inputEl) {
			inputEl.value = searchTerm
			inputEl.classList.add('filled')
		}

		const runInitialSearch = (): void => {
			if (!this._searchStillApplies(generation)) {
				return
			}

			try {
				this.searchByAmbiguousTerm(searchTerm)
			} catch (err) {
				window.console.warn(`WARN: Failed to initialize Dealer Locator search: ${err}`)
			}
		}

		if (window.gmxGoogleMapApiLoaderSingleton?.loadStatus !== 'loaded') {
			this.pendingApiSearchHandler = runInitialSearch
			window.addEventListener('gmxGoogleMapApiLoaded', runInitialSearch, { once: true })
		} else {
			runInitialSearch()
		}
	}
}

if (!customElements.get('gmx-dealer-locator')) {
	customElements.define('gmx-dealer-locator', GMXDealerLocator)
}
